// THE "MAKE … INACTIVE?" CONFIRMATION — one dialog for a Code, an Account and a
// Department (owner, 2026-09-25 — docs/decisions.md D-175, D-176).
//
// *"The dialog should list them specially though — not just count them. And
// please actually make it look nice."* So every record that goes Inactive with
// the subject is LISTED, by name, under its own heading — Codes, Departments,
// Accounts, Coding Rules — in one bordered panel that scrolls inside the dialog
// when the list is long, rather than growing the dialog or running the names
// together as a sentence. Ids are set in the monospace face with tabular
// figures, so a column of `601200-10-100`s lines up and reads as ids.
//
// THE DECISIONDIALOG LAYOUT CONVENTIONS (`DecisionDialog.tsx`): one column,
// every part 16px apart and the buttons 24px below, the pop-up form spacing
// (`dialogForm.ts`, D-349), and nothing else adding space; the footer takes the
// whole row (`fluid`) and no button wraps or shrinks, so a long label such as
// *Make Account + 3 Codes + 1 Coding Rule Inactive* widens the footer instead
// of breaking onto a second line. The confirm button is the blue primary,
// because the change is reversible.
//
// The dialog words nothing about which records go — that is the server's
// answer, read before it opens (`previewInactiveCascade`, for an Account, a
// Department or a Code alike, D-180). It only lays them out.
import type { ReactNode } from 'react'
import {
  Body1,
  Button,
  Caption1,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { AppDialog } from './AppDialog'
import { ErrorBanner } from './States'
import { FrozenWhileClosed } from './FrozenWhileClosed'
import { useDialogFormStyles } from './dialogForm'

/** One record in a section: its id (monospace), its name, and an optional detail. */
interface InactiveListItem {
  key: string
  id?: string
  primary: string
  secondary?: string
}

/** One heading and the records under it. An empty section is not drawn. */
export interface InactiveListSection {
  heading: 'Codes' | 'Departments' | 'Accounts' | 'Coding Rules'
  items: InactiveListItem[]
}

const useStyles = makeStyles({
  surface: { maxWidth: '640px', width: 'calc(100vw - 32px)' },
  intro: { display: 'block', margin: 0 },
  // THE LIST PANEL. Bounded, so a department with forty Codes scrolls inside
  // the dialog instead of pushing the buttons off the screen.
  panel: {
    maxHeight: '44vh',
    overflowY: 'auto',
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  heading: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  list: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' },
  row: {
    display: 'grid',
    // The id column is as wide as its longest id; the name takes the rest.
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    columnGap: tokens.spacingHorizontalM,
    alignItems: 'baseline',
    padding: `${tokens.spacingVerticalXXS} 0`,
    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke3}`,
    ':last-child': { borderBottom: 'none' },
  },
  // A row with no id (a Coding Rule): the vendor, and under it what it matches.
  rowNoId: { display: 'flex', flexDirection: 'column', rowGap: '0px' },
  id: {
    fontFamily: tokens.fontFamilyMonospace,
    fontVariantNumeric: 'tabular-nums',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
  },
  primary: { fontSize: tokens.fontSizeBase300, minWidth: 0, overflowWrap: 'anywhere' },
  secondary: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, overflowWrap: 'anywhere' },
  notes: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  note: { display: 'block', color: tokens.colorNeutralForeground3 },
  // ONE LINE PER BUTTON, whatever the label (QA C2-03, B-03).
  button: { whiteSpace: 'nowrap', flexShrink: 0 },
})

export function MakeInactiveDialog({
  open,
  noun,
  intro,
  sections,
  notes,
  confirmLabel,
  loading,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  open: boolean
  /** "Code", "Account" or "Department" — the title reads *Make {noun} Inactive?* */
  noun: string
  /** The opening sentence, naming the subject. */
  intro: ReactNode
  sections: InactiveListSection[]
  /** Short sentences under the list: invoices in progress, reactivation. */
  notes: string[]
  confirmLabel: string
  /** The list is still being read; the confirm waits for it. */
  loading?: boolean
  busy: boolean
  error: string | null
  onConfirm: () => void
  onCancel: () => void
}) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const shown = sections.filter((section) => section.items.length > 0)
  return (
    <AppDialog open={open} onOpenChange={(_, d) => { if (!d.open && !busy) onCancel() }}>
      <DialogSurface className={s.surface}>
        <FrozenWhileClosed open={open}>
          <DialogBody>
            <DialogTitle>{`Make ${noun} Inactive?`}</DialogTitle>
            <DialogContent className={mergeClasses(f.stack, f.beforeActions)}>
              <ErrorBanner message={error} />
              <Body1 as="p" className={s.intro}>
                {intro}
              </Body1>
              {loading ? (
                <Spinner size="tiny" label="Checking what uses it…" labelPosition="after" />
              ) : (
                shown.length > 0 && (
                  <div className={s.panel} role="group" aria-label="Also made Inactive">
                    {shown.map((section) => (
                      <section key={section.heading} className={s.section} aria-label={section.heading}>
                        <h3 className={s.heading}>{`${section.heading} (${section.items.length})`}</h3>
                        <ul className={s.list}>
                          {section.items.map((item) =>
                            item.id !== undefined ? (
                              <li key={item.key} className={s.row}>
                                <span className={s.id}>{item.id}</span>
                                <span className={s.primary}>{item.primary}</span>
                              </li>
                            ) : (
                              <li key={item.key} className={mergeClasses(s.row, s.rowNoId)}>
                                <span className={s.primary}>{item.primary}</span>
                                {item.secondary !== undefined && <span className={s.secondary}>{item.secondary}</span>}
                              </li>
                            ),
                          )}
                        </ul>
                      </section>
                    ))}
                  </div>
                )
              )}
              {notes.length > 0 && (
                <div className={s.notes}>
                  {notes.map((note) => (
                    <Caption1 key={note} className={s.note}>
                      {note}
                    </Caption1>
                  ))}
                </div>
              )}
            </DialogContent>
            <DialogActions fluid>
              <Button appearance="secondary" className={s.button} onClick={onCancel} disabled={busy}>
                Cancel
              </Button>
              <Button appearance="primary" className={s.button} onClick={onConfirm} disabled={busy || loading}>
                {confirmLabel}
              </Button>
            </DialogActions>
          </DialogBody>
        </FrozenWhileClosed>
      </DialogSurface>
    </AppDialog>
  )
}
